import { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";

const AuthContext = createContext(null);

const TOKEN_KEY = "unity_token";
const USERNAME_KEY = "unity_username";
const TOKEN_REFRESH_INTERVAL = 6 * 60 * 60 * 1000; // Refresh every 6 hours
const MAX_401_BEFORE_LOGOUT = 3;

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const consecutiveFailsRef = useRef(0);
  const refreshTimerRef = useRef(null);
  const isLoggedOutRef = useRef(false);

  const clearTimers = useCallback(() => {
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USERNAME_KEY);
    setIsAuthenticated(false);
    consecutiveFailsRef.current = 0;
    isLoggedOutRef.current = true;
    clearTimers();
  }, [clearTimers]);

  const refreshToken = useCallback(async () => {
    // Don't try to refresh if already logged out
    if (isLoggedOutRef.current) return;
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;

    try {
      const res = await api.refreshToken();
      if (res.data?.token) {
        localStorage.setItem(TOKEN_KEY, res.data.token);
        consecutiveFailsRef.current = 0;
      }
    } catch {
      // Token refresh failed - token still valid until expiry
    }
  }, []);

  const handleLogin = useCallback((token, username) => {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USERNAME_KEY, username);
    setIsAuthenticated(true);
    consecutiveFailsRef.current = 0;
    isLoggedOutRef.current = false;

    // Start periodic token refresh
    clearTimers();
    refreshTimerRef.current = setInterval(refreshToken, TOKEN_REFRESH_INTERVAL);
  }, [refreshToken, clearTimers]);

  const handleAuthError = useCallback(() => {
    // Prevent cascading logouts
    if (isLoggedOutRef.current) return;

    consecutiveFailsRef.current += 1;
    if (consecutiveFailsRef.current >= MAX_401_BEFORE_LOGOUT) {
      toast.error("Sesja wygasla. Zaloguj sie ponownie.");
      handleLogout();
    }
  }, [handleLogout]);

  const handleAuthSuccess = useCallback(() => {
    consecutiveFailsRef.current = 0;
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem(TOKEN_KEY);
      if (token) {
        try {
          await api.verifyAuth();
          setIsAuthenticated(true);
          isLoggedOutRef.current = false;
          refreshTimerRef.current = setInterval(refreshToken, TOKEN_REFRESH_INTERVAL);
        } catch {
          // Token invalid/expired - clean up
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(USERNAME_KEY);
          isLoggedOutRef.current = true;
        }
      }
      setIsCheckingAuth(false);
    };
    checkAuth();
    return clearTimers;
  }, [refreshToken, clearTimers]);

  // Sync logout across browser tabs
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === TOKEN_KEY && !e.newValue) {
        setIsAuthenticated(false);
        isLoggedOutRef.current = true;
        clearTimers();
      } else if (e.key === TOKEN_KEY && e.newValue) {
        setIsAuthenticated(true);
        isLoggedOutRef.current = false;
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [clearTimers]);

  const contextValue = useMemo(
    () => ({ isAuthenticated, isCheckingAuth, handleLogin, handleLogout, handleAuthError, handleAuthSuccess }),
    [isAuthenticated, isCheckingAuth, handleLogin, handleLogout, handleAuthError, handleAuthSuccess]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}
